import React, { useCallback, useEffect, useState } from 'react';
import './RealizationsPage.css';
import { authHeader } from '../services/authService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend.ouest-pro-event.fr';

const getFullImageUrl = (relativeUrl) => {
  if (!relativeUrl) return '';
  if (relativeUrl.startsWith('http')) return relativeUrl;
  return `${BACKEND_URL}${relativeUrl}`;
};

function RealizationsPage() {
  const [realizations, setRealizations] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [editingRealization, setEditingRealization] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchRealizations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
        console.log(`${BACKEND_URL}/admin/realizations`)
        
      const response = await fetch(`${BACKEND_URL}/admin/realizations`, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Erreur lors du chargement des réalisations");
      }
      const data = await response.json();
      setRealizations(Array.isArray(data.realizations) ? data.realizations : []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Impossible de charger les réalisations");
      setRealizations([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRealizations();
  }, [fetchRealizations]);

  useEffect(() => () => {
    photoPreviews.forEach((src) => URL.revokeObjectURL(src));
  }, [photoPreviews]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSelectedPhotos([]);
    photoPreviews.forEach((src) => URL.revokeObjectURL(src));
    setPhotoPreviews([]);
    setEditingRealization(null);
    setError(null);
  };

  const handlePhotoChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const newFiles = [];
    const newPreviews = [];

    files.forEach((file) => {
      const isDuplicate = selectedPhotos.some(
        (existing) => existing.name === file.name && existing.size === file.size
      );
      if (!isDuplicate) {
        newFiles.push(file);
        newPreviews.push(URL.createObjectURL(file));
      }
    });

    if (newFiles.length === 0) {
      return;
    }

    setSelectedPhotos((prev) => [...prev, ...newFiles]);
    setPhotoPreviews((prev) => [...prev, ...newPreviews]);
  };

  const handleRemoveSelectedPhoto = (index) => {
    URL.revokeObjectURL(photoPreviews[index]);
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
    setSelectedPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (realization) => {
    setEditingRealization(realization);
    setTitle(realization.title || '');
    setDescription(realization.description || '');
    setSelectedPhotos([]);
    photoPreviews.forEach((src) => URL.revokeObjectURL(src));
    setPhotoPreviews([]);
    setError(null);
  };

  const handleDeleteRealization = async (realizationId) => {
    if (!window.confirm('Supprimer cette réalisation ?')) return;
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/admin/realizations/${realizationId}`, {
        method: 'DELETE',
        headers: {
          ...authHeader(),
        },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Erreur lors de la suppression de la réalisation");
      }
      if (editingRealization && editingRealization.id === realizationId) {
        resetForm();
      }
      await fetchRealizations();
    } catch (err) {
      console.error(err);
      setError(err.message || "Impossible de supprimer la réalisation");
    }
  };

  const handleDeletePhoto = async (realizationId, photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    setError(null);
    try {
      const response = await fetch(
        `${BACKEND_URL}/admin/realizations/${realizationId}/photos/${photoId}`,
        {
          method: 'DELETE',
          headers: {
            ...authHeader(),
          },
        }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Erreur lors de la suppression de la photo");
      }
      const data = await response.json();
      const updated = data.realization;
      setRealizations((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
      if (editingRealization && editingRealization.id === updated.id) {
        setEditingRealization(updated);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Impossible de supprimer la photo");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Le titre est obligatoire.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
      };

      const method = editingRealization ? 'PUT' : 'POST';
      const url = editingRealization
        ? `${BACKEND_URL}/admin/realizations/${editingRealization.id}`
        : `${BACKEND_URL}/admin/realizations`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Erreur lors de l'enregistrement de la réalisation");
      }

      let data = await response.json();
      let realizationId = editingRealization ? editingRealization.id : data.realization?.id;
      let latestRealization = data.realization;

      if (selectedPhotos.length > 0 && realizationId) {
        const formData = new FormData();
        selectedPhotos.forEach((photo) => formData.append('photos', photo));
        const photoResponse = await fetch(
          `${BACKEND_URL}/admin/realizations/${realizationId}/photos`,
          {
            method: 'POST',
            headers: {
              ...authHeader(),
            },
            body: formData,
          }
        );
        if (!photoResponse.ok) {
          const errData = await photoResponse.json().catch(() => ({}));
          throw new Error(errData.error || "Erreur lors du téléversement des photos");
        }
        data = await photoResponse.json();
        latestRealization = data.realization;
      }

      await fetchRealizations();
      resetForm();
      if (latestRealization) {
        setRealizations((prev) => {
          const exists = prev.some((item) => item.id === latestRealization.id);
          if (exists) {
            return prev.map((item) =>
              item.id === latestRealization.id ? latestRealization : item
            );
          }
          return [latestRealization, ...prev];
        });
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Impossible d'enregistrer la réalisation");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="realizations-page">
      <div className="page-header">
        <h1>Réalisations</h1>
        <p>Créez et gérez vos réalisations avec plusieurs photos.</p>
      </div>

      {error && <div className="error-alert">{error}</div>}

      <div className="main-content">
        <div className="card form-card">
          <div className="card-header">
            <h2>{editingRealization ? 'Modifier la réalisation' : 'Nouvelle réalisation'}</h2>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label htmlFor="title">Titre *</label>
                  <input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Titre de la réalisation"
                  />
                </div>
                <div className="form-group full-width">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Description courte"
                  />
                </div>
              </div>

              <div className="photos-section">
                <h3>Photos</h3>
                <div className="photos-upload">
                  <label htmlFor="realizationPhotos" className="btn btn-secondary btn-sm">
                    Choisir des fichiers
                  </label>
                  <input
                    id="realizationPhotos"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                  />
                  <span className="help-text">Ajoutez une ou plusieurs photos (5 Mo max par photo).</span>
                </div>

                {photoPreviews.length > 0 && (
                  <div className="photos-preview">
                    <h4>Nouvelles photos</h4>
                    <div className="photos-grid">
                      {photoPreviews.map((src, index) => (
                        <div key={index} className="photo-item new">
                          <img src={src} alt={`Nouvelle ${index + 1}`} />
                          <button
                            type="button"
                            className="btn-icon delete"
                            onClick={() => handleRemoveSelectedPhoto(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {editingRealization?.photos?.length > 0 && (
                  <div className="current-photos">
                    <h4>Photos actuelles</h4>
                    <div className="photos-grid">
                      {editingRealization.photos.map((photo) => (
                        <div key={photo.id} className="photo-item current">
                          <img src={getFullImageUrl(photo.url)} alt="" />
                          <button
                            type="button"
                            className="btn-icon delete"
                            onClick={() => handleDeletePhoto(editingRealization.id, photo.id)}
                            disabled={isSubmitting}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success" disabled={isSubmitting}>
                  {isSubmitting ? 'Enregistrement...' : editingRealization ? 'Enregistrer' : 'Créer'}
                </button>
                {editingRealization && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="card list-card">
          <div className="card-header">
            <h2>Réalisations existantes</h2>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="loading-indicator">Chargement...</div>
            ) : realizations.length === 0 ? (
              <p>Aucune réalisation enregistrée pour le moment.</p>
            ) : (
              <div className="table-container">
                <table className="realizations-table">
                  <thead>
                    <tr>
                      <th>Titre</th>
                      <th>Description</th>
                      <th>Photos</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {realizations.map((realization) => (
                      <tr key={realization.id}>
                        <td className="title-cell">{realization.title}</td>
                        <td className="description-cell">
                          {realization.description ? realization.description : <span className="muted">—</span>}
                        </td>
                        <td className="photos-cell">
                          <div className="thumbnail-container">
                            {realization.photos && realization.photos.length > 0 ? (
                              realization.photos.slice(0, 3).map((photo) => (
                                <img
                                  key={photo.id}
                                  src={getFullImageUrl(photo.url)}
                                  alt="Miniature"
                                  className="thumbnail"
                                />
                              ))
                            ) : (
                              <span className="muted">Aucune photo</span>
                            )}
                            {realization.photos && realization.photos.length > 3 && (
                              <span className="more-count">+{realization.photos.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td className="actions-cell">
                          <button
                            type="button"
                            className="btn btn-edit"
                            onClick={() => handleEdit(realization)}
                            disabled={isSubmitting}
                          >
                            Modifier
                          </button>
                          <button
                            type="button"
                            className="btn btn-delete"
                            onClick={() => handleDeleteRealization(realization.id)}
                            disabled={isSubmitting}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RealizationsPage;